import React from 'react';
import { useTranslation } from 'react-i18next';
import { Info, Loader2 } from 'lucide-react';

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

import { SectionCard } from '../../../../components/design-system/admin/SectionCard';
import { formatDateTime, formatNumber } from '../../../../components/design-system/admin/format';
import { useContentStudioApi } from '../../../../common/hooks/use-content-studio-api.hook';
import { CsCreditEntry } from '../../../../common/models/content-studio.model';

interface VendorCreditsPanelProps {
    /** ObjectId string of the vendor to inspect / adjust. */
    vendorId: string | undefined;
    /** Wraps the body in the standard card container (used on the edit
     *  page); when false the panel renders bare for embedding inside an
     *  existing card / dialog (e.g. inline on the vendor list row). */
    wrapInCard?: boolean;
    /** Optional notification when a Set succeeds — used by parents that
     *  want to refresh sibling data (e.g. the vendor list table). */
    onAdjusted?: (newBalance: number) => void;
}

/**
 * Super-admin panel showing a vendor's Content Studio credit balance +
 * recent ledger history with a "Set credits…" dialog that adjusts the
 * balance to a target value via the audited admin endpoint. The balance
 * shown reflects all entry types (seed, grant, hold, debit, refund,
 * admin-adjust) — the same aggregate the vendor sees in their own UI.
 */
export const VendorCreditsPanel: React.FC<VendorCreditsPanelProps> = ({
    vendorId,
    wrapInCard = true,
    onAdjusted,
}) => {
    const { t } = useTranslation();
    const csApi = useContentStudioApi();

    const [loading, setLoading] = React.useState(false);
    const [error, setError] = React.useState<string | null>(null);
    const [balance, setBalance] = React.useState<number | null>(null);
    const [history, setHistory] = React.useState<CsCreditEntry[]>([]);

    const [dialogOpen, setDialogOpen] = React.useState(false);
    const [targetInput, setTargetInput] = React.useState<string>('');
    const [reasonInput, setReasonInput] = React.useState<string>('');
    const [submitting, setSubmitting] = React.useState(false);

    const refresh = React.useCallback(async () => {
        if (!vendorId) return;
        setLoading(true);
        setError(null);
        try {
            const r = await csApi.adminGetCreditsForVendor(vendorId);
            setBalance(r.balance);
            setHistory(Array.isArray(r.history) ? r.history : []);
        } catch (err: any) {
            setError(err?.response?.data?.message || err?.message || t('ADMIN_VENDOREDIT.CREDITS.LOAD_FAILED'));
        } finally {
            setLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vendorId]);

    React.useEffect(() => {
        if (vendorId) refresh();
        else {
            setBalance(null);
            setHistory([]);
        }
    }, [vendorId, refresh]);

    const openDialog = () => {
        setTargetInput(balance != null ? String(balance) : '0');
        setReasonInput('');
        setDialogOpen(true);
    };

    const submit = async () => {
        if (!vendorId) return;
        const target = Number(targetInput);
        if (!Number.isFinite(target) || target < 0) {
            setError(t('ADMIN_VENDOREDIT.CREDITS.TARGET_INVALID'));
            return;
        }
        setSubmitting(true);
        try {
            const r = await csApi.adminSetCreditsForVendor(
                vendorId,
                Math.floor(target),
                reasonInput.trim() || undefined,
            );
            setBalance(r.newBalance);
            setDialogOpen(false);
            onAdjusted?.(r.newBalance);
            // Refresh history so the new admin-adjust entry shows up.
            await refresh();
        } catch (err: any) {
            setError(err?.response?.data?.message || err?.message || t('ADMIN_VENDOREDIT.CREDITS.SET_FAILED'));
        } finally {
            setSubmitting(false);
        }
    };

    const setButton = (
        <Button variant="outline" size="sm" onClick={openDialog} disabled={!vendorId || loading}>
            {t('ADMIN_VENDOREDIT.CREDITS.SET')}
        </Button>
    );

    const body = (
        <>
            <div className="mb-4 flex items-end justify-between gap-4">
                <div>
                    <p className="mb-1 text-xs text-muted-foreground">{t('ADMIN_VENDOREDIT.CREDITS.BALANCE')}</p>
                    <p className="flex items-baseline gap-1.5 font-heading text-3xl leading-none font-medium tracking-tight tabular-nums">
                        {loading ? (
                            <Loader2 className="size-5 animate-spin text-muted-foreground" />
                        ) : (
                            <span data-testid="vendor-credits-balance">{balance != null ? formatNumber(balance) : '—'}</span>
                        )}
                        <span className="text-sm font-normal text-muted-foreground">{t('ADMIN_VENDOREDIT.CREDITS.UNIT')}</span>
                    </p>
                </div>
                {!wrapInCard && setButton}
            </div>

            {error && <p className="mb-3 text-[13px] text-destructive">{error}</p>}

            {history.length > 0 && (
                <div>
                    <p className="mb-2 text-xs text-muted-foreground">{t('ADMIN_VENDOREDIT.CREDITS.RECENT')}</p>
                    <div className="max-h-[280px] overflow-y-auto rounded-lg border">
                        {history.map((entry) => (
                            <div
                                key={(entry as any).entryId || (entry as any)._id}
                                className="grid grid-cols-[80px_64px_1fr] items-center gap-x-3 gap-y-0.5 border-b px-3 py-2 text-[13px] last:border-b-0 sm:grid-cols-[90px_72px_1fr_auto]"
                            >
                                <span className="text-xs text-muted-foreground">{entry.type}</span>
                                <span
                                    className={cn(
                                        'text-end font-medium tabular-nums',
                                        entry.amount > 0
                                            ? 'text-success'
                                            : entry.amount < 0
                                                ? 'text-destructive'
                                                : 'text-muted-foreground',
                                    )}
                                >
                                    {entry.amount > 0 ? '+' : ''}
                                    {entry.amount}
                                </span>
                                <span className="truncate" title={entry.reason}>
                                    {entry.reason}
                                </span>
                                <span className="col-span-3 text-xs text-muted-foreground sm:col-span-1">
                                    {entry.createdAt ? formatDateTime(entry.createdAt) : ''}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            <Dialog
                open={dialogOpen}
                onOpenChange={(open) => {
                    if (!open && !submitting) setDialogOpen(false);
                }}
            >
                <DialogContent className="sm:max-w-md">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-lg font-medium tracking-tight">
                            {t('ADMIN_VENDOREDIT.CREDITS.DIALOG_TITLE')}
                        </DialogTitle>
                        <DialogDescription className="leading-relaxed">
                            {t('ADMIN_VENDOREDIT.CREDITS.DIALOG_BODY', { type: 'type=admin-adjust' })}
                        </DialogDescription>
                    </DialogHeader>

                    {/* Monthly-reset caveat — this is a one-off, not the recurring amount. */}
                    <div className="flex items-start gap-2 rounded-lg border p-3">
                        <Info className="mt-px size-4 shrink-0 text-muted-foreground" />
                        <p className="text-[13px] leading-relaxed text-muted-foreground">
                            {t('ADMIN_VENDOREDIT.CREDITS.RESET_NOTE', { override: t('ADMIN_VENDOREDIT.OVERRIDES.LIMIT.monthlyCredits') })}
                        </p>
                    </div>

                    <div>
                        <Label htmlFor="vendor-credits-target" className="mb-1.5 text-xs font-medium text-muted-foreground">
                            {t('ADMIN_VENDOREDIT.CREDITS.TARGET')}
                        </Label>
                        <Input
                            id="vendor-credits-target"
                            type="number"
                            min={0}
                            step={1}
                            value={targetInput}
                            onChange={(e) => setTargetInput(e.target.value)}
                        />
                    </div>

                    <div>
                        <Label htmlFor="vendor-credits-reason" className="mb-1.5 text-xs font-medium text-muted-foreground">
                            {t('ADMIN_VENDOREDIT.CREDITS.REASON')}
                        </Label>
                        <Input
                            id="vendor-credits-reason"
                            value={reasonInput}
                            onChange={(e) => setReasonInput(e.target.value)}
                            placeholder={t('ADMIN_VENDOREDIT.CREDITS.REASON_PLACEHOLDER')}
                        />
                        {balance != null && Number.isFinite(Number(targetInput)) && (
                            <p className="mt-1.5 text-xs text-muted-foreground tabular-nums">
                                {t('ADMIN_VENDOREDIT.CREDITS.DELTA', { delta: Math.floor(Number(targetInput)) - balance })}
                            </p>
                        )}
                    </div>

                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={submitting}>
                            {t('ADMIN_SHELL.COMMON.CANCEL')}
                        </Button>
                        <Button onClick={submit} disabled={submitting}>
                            {submitting ? <Loader2 className="size-4 animate-spin" /> : t('ADMIN_SHELL.COMMON.SAVE')}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </>
    );

    if (!wrapInCard) return body;

    return (
        <SectionCard
            title={t('ADMIN_VENDOREDIT.CREDITS.TITLE')}
            description={t('ADMIN_VENDOREDIT.CREDITS.DESCRIPTION')}
            action={setButton}
            testId="vendor-credits-card"
        >
            {body}
        </SectionCard>
    );
};
